import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiCalendar, FiMessageSquare, FiFlag, FiTrendingUp, FiClock } from 'react-icons/fi';
import AdminService from '../services/AdminService';

const StatCard = ({ icon: Icon, label, value, trend, color }) => (
  <div className="stat-card rounded-2xl p-5 bg-white border border-slate-200 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {trend && (
        <span className="text-[11px] font-bold text-emerald-700 flex items-center gap-1 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
          <FiTrendingUp className="w-3 h-3" /> {trend}
        </span>
      )}
    </div>
    <p className="text-2xl font-extrabold text-slate-900">{value}</p>
    <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider mt-1">{label}</p>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [activity, setActivity] = useState([]);
  const [timeframe, setTimeframe] = useState('7d');
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminService.getDashboardStats(timeframe);
      if (res.stats) setStats(res.stats);
      if (res.growthData) setGrowthData(res.growthData);
      if (res.recentActivity) setActivity(res.recentActivity);
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-3 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  const maxGrowth = Math.max(...growthData.map(d => d.users), 1);

  return (
    <div className="space-y-6 max-w-7xl text-left">
      {/* Header */}
      <div className="flex items-center justify-between bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Dashboard Overview</h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Platform metrics, events & growth analytics</p>
        </div>
        <div className="flex gap-1.5 bg-slate-100 rounded-xl p-1 border border-slate-200">
          {['7d', '30d', 'year', 'lifetime'].map((t) => (
            <button
              key={t}
              onClick={() => setTimeframe(t)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition ${timeframe === t ? 'bg-purple-700 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`}
            >
              {t === '7d' ? '7 Days' : t === '30d' ? '30 Days' : t === 'year' ? 'Year' : 'All Time'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FiUsers} label="Total Users" value={stats?.totalUsers?.toLocaleString() || '0'} trend={stats?.userTrend} color="bg-purple-700" />
        <StatCard icon={FiCalendar} label="Active Posts" value={stats?.activePosts?.toLocaleString() || '0'} color="bg-blue-600" />
        <StatCard icon={FiMessageSquare} label="Total Messages" value={stats?.totalMessages?.toLocaleString() || '0'} color="bg-emerald-600" />
        <StatCard icon={FiFlag} label="Pending Reports" value={stats?.pendingReports || '0'} color="bg-rose-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Growth Chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-extrabold text-slate-900 mb-4">User Growth Trend</h3>
          <div className="flex items-end gap-1.5 h-44">
            {growthData.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] text-slate-600 font-bold">{d.users}</span>
                <div
                  className="w-full bg-purple-600 rounded-t-md transition-all duration-500"
                  style={{ height: `${Math.max((d.users / maxGrowth) * 100, 4)}%`, minHeight: '4px' }}
                />
                <span className="text-[9px] text-slate-500 font-bold uppercase">{d.name}</span>
              </div>
            ))}
            {growthData.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-xs text-slate-400">No data available</div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-extrabold text-slate-900 mb-4">Recent Platform Activity</h3>
          <div className="space-y-3">
            {activity.length === 0 && <p className="text-xs text-slate-400">No recent activity</p>}
            {activity.map((item, i) => (
              <div key={i} className="flex items-start gap-3 border-b border-slate-100 pb-2.5 last:border-0">
                <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${item.type === 'user' ? 'bg-purple-600' : item.type === 'post' ? 'bg-blue-600' : 'bg-rose-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-900 truncate">{item.text}</p>
                  <p className="text-[10px] text-slate-400 font-medium flex items-center gap-1 mt-0.5">
                    <FiClock className="w-2.5 h-2.5 text-slate-400" /> {item.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
